import React, { useState, useEffect, useRef } from "react";
import { motion, useMotionValue, useAnimationFrame } from "motion/react";
import { useResponsive } from "../utils/responsiveContext";
import { skills } from "../utils/skills";

const FloatingSkill = ({ skill, containerSize }) => {
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const velocityRef = useRef({ x: 0, y: 0 });
  const isInitialized = useRef(false);
  const skillRef = useRef(null);
  const skillDimensions = useRef({ width: 0, height: 0 });
  const { isMobile } = useResponsive();

  useEffect(() => {
    if (
      containerSize.width > 0 &&
      containerSize.height > 0 &&
      !isInitialized.current &&
      skillRef.current
    ) {
      const { offsetWidth, offsetHeight } = skillRef.current;
      skillDimensions.current = { width: offsetWidth, height: offsetHeight };

      const initialX = Math.random() * (containerSize.width - offsetWidth);
      const initialY = Math.random() * (containerSize.height - offsetHeight);

      x.set(initialX);
      y.set(initialY);

      velocityRef.current = {
        x: (Math.random() - 0.5) * 3,
        y: (Math.random() - 0.5) * 3,
      };

      isInitialized.current = true;
    }
  }, [containerSize, x, y]);

  useAnimationFrame(() => {
    if (
      !isInitialized.current ||
      containerSize.width === 0 ||
      !skillRef.current
    )
      return;

    const currentX = x.get();
    const currentY = y.get();
    const velocity = velocityRef.current;
    const { width, height } = skillDimensions.current;

    const newX = currentX + velocity.x;
    const newY = currentY + velocity.y;

    if (newX <= 0 || newX >= containerSize.width - width) {
      velocity.x = -velocity.x;
    }
    if (newY <= 0 || newY >= containerSize.height - height) {
      velocity.y = -velocity.y;
    }

    x.set(Math.max(0, Math.min(containerSize.width - width, newX)));
    y.set(Math.max(0, Math.min(containerSize.height - height, newY)));
  });

  return (
    <motion.div
      ref={skillRef}
      initial={{ opacity: 0, scale: 0.2 }}
      className="absolute flex items-center justify-center rounded-full select-none"
      style={{ x, y }}
      whileHover={{
        scale: 1.4,
        transition: { duration: 0.1 },
      }}
      whileInView={{
        opacity: 1,
        scale: 1,
        transition: { duration: 0.5, ease: "easeInOut" },
      }}
      viewport={{ once: false, amount: 0.2 }}
      transition={{
        duration: 0.5,
        ease: "easeInOut",
      }}
    >
      <p
        className={`flex items-center gap-2 ${
          isMobile ? "p-1" : "p-2"
        } rounded-full text-shade-4 bg-shade-3 `}
      >
        <span
          className={`${
            isMobile ? "text-md p-1" : "text-2xl p-2"
          } rounded-full bg-shade-4 shadow-custom-2`}
          style={{ color: skill.color }}
        >
          {skill.icon}
        </span>
        <span className={`${isMobile ? "text-xs" : "text-sm"} text-white`}>
          {skill.name}
        </span>
      </p>
    </motion.div>
  );
};

export const Skills = () => {
  const [containerSize, setContainerSize] = useState({ width: 0, height: 0 });

  useEffect(() => {
    const updateSize = () => {
      const container = document.querySelector(".skills-container");
      if (container) {
        const { offsetWidth: width, offsetHeight: height } = container;
        setContainerSize({ width, height });
      }
    };

    updateSize();
    window.addEventListener("resize", updateSize);
    return () => window.removeEventListener("resize", updateSize);
  }, []);

  return (
    <div className="w-full h-full relative overflow-hidden skills-container p-2">
      {skills.map((skill) => (
        <FloatingSkill
          key={skill.name}
          skill={skill}
          containerSize={containerSize}
        />
      ))}
    </div>
  );
};
